// src/scenes/drivers/Drivers.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupIcon from "@mui/icons-material/Group";
import BadgeIcon from "@mui/icons-material/Badge";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { getDriversByBoss, updateDriver } from "../../services/driverService";
import { getLicensesByBoss, assignDriverToLicense } from "../../services/licenseService";
import { useSelector } from "react-redux";
import { tokens } from "../../theme";

/* ===== Paletas coherentes ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ===== Widget ===== */
function StatCard({ icon, label, value, palette, highlight = false, isMobile = false }) {
  return (
    <Box
      sx={{
        p: isMobile ? 1.75 : 2,
        height: "100%",
        borderRadius: "12px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 1 : 1.25,
        minHeight: 76,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.95, display: "grid", placeItems: "center" }}>{icon}</Box>
      <Box sx={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
        <Typography
          variant="caption"
          sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Card contenedora ===== */
function Card({ title, palette, children, action }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: action ? "space-between" : "flex-start",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
          {title}
        </Typography>
        {action && <Box sx={{ display: "flex", alignItems: "center" }}>{action}</Box>}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
        {children}
      </Box>
    </Box>
  );
}

/* ===== Estilo DataGrid (altura completa) ===== */
const dgSx = (palette) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  "& .MuiDataGrid-root": {
    border: "none",
    color: palette.text,
    backgroundColor: "transparent",
    height: "100%",
    width: "100%",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${palette.cardBorder}`,
    color: palette.label,
    fontWeight: 700,
  },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${palette.cardBorder}`,
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-overlay": {
    backgroundColor: "transparent",
    color: palette.label,
  },
});

const Drivers = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  useSelector((state) => state.user); // Trigger redux subscription for auth-protected requests

  // tabla
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // menú y modal
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // licencias en modal
  const [licenses, setLicenses] = useState([]);
  const [selectedLicenses, setSelectedLicenses] = useState([]);

  // edición
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    userId: "",
    name: "",
    lastName: "",
    hireDate: "",
    username: "",
    password: "",
    revenuePercentage: 40,
  });

  const isDark = theme.palette.mode === "dark";
  const modalCardBg = isDark ? "#1e2a40" : "#fdfcfc";
  const modalBorder = isDark ? "#2b3b55" : "#d1d5db";
  const modalAccent = colors.blueAccent[400];
  const modalText = isDark ? colors.grey[100] : "#000000";
  const modalLabel = isDark ? "#ffffff" : "#000000";
  const modalInputBg = isDark ? colors.primary[500] : "#ececec";
  const modalInputStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: modalInputBg,
      color: modalText,
      "& fieldset": { borderColor: modalBorder },
      "&:hover fieldset": { borderColor: modalBorder },
      "&.Mui-focused fieldset": { borderColor: modalAccent, borderWidth: 1.5 },
    },
    "& .MuiInputBase-input": { color: modalText },
    "& .MuiInputLabel-root": { color: modalLabel },
    "& .MuiInputLabel-root.Mui-focused": { color: modalText },
  };

  // snackbar
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const openMenu = Boolean(anchorEl);
  const handleMenuOpen = (e, driverId) => {
    setAnchorEl(e.currentTarget);
    setSelectedDriver(driverId);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenModal = async () => {
    handleMenuClose();
    try {
      const lic = await getLicensesByBoss();
      setLicenses(lic || []);
      setModalOpen(true);
    } catch (err) {
      setSnack({
        open: true,
        severity: "warning",
        message:
          "No se han encontrado licencias para su usuario. No es posible asignar ninguna licencia al conductor.",
      });
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLicenses([]);
    setSelectedDriver(null);
  };

  const handleAssign = async () => {
    if (!selectedLicenses.length) return;
    try {
      await Promise.all(selectedLicenses.map((lic) => assignDriverToLicense(lic, selectedDriver)));
      // recargar lista
      const data = await getDriversByBoss();
      const rows = (data || []).map((d, idx) => {
        const hireDateRaw = d.hireDate ? new Date(d.hireDate).toISOString().slice(0, 10) : "";
        return {
          id: d._id || `drv_${idx}`,
          name: d.name ?? "—",
          lastName: d.lastName ?? "—",
          hireDate: hireDateRaw ? new Date(hireDateRaw).toLocaleDateString() : "—",
          hireDateRaw,
          licensesCount: d.licenses?.length ?? 0,
          closuresCount: d.accountingClosures?.length ?? 0,
          totalEarned: d.totalEarned ?? 0,
          totalKmDriven: d.totalKmDriven ?? 0,
          daysWorked: d.daysWorked ?? 0,
          revenuePercentage: d.revenuePercentage ?? 40,
          username: d.user?.username || "",
          userId: d.user?._id || d.user || "",
        };
      });
      setDrivers(rows);
      setSnack({
        open: true,
        message: "Licencias asignadas con éxito",
        severity: "success",
      });
      handleCloseModal();
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleOpenEdit = () => {
    const driver = drivers.find((d) => d.id === selectedDriver);
    if (!driver) return;
    setEditForm({
      id: driver.id,
      userId: driver.userId || "",
      name: driver.name || "",
      lastName: driver.lastName || "",
      hireDate: driver.hireDateRaw || "",
      username: driver.username || "",
      password: "",
      revenuePercentage: driver.revenuePercentage ?? 40,
    });
    handleMenuClose();
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: name === "revenuePercentage" ? Number(value) : value,
    }));
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditForm((prev) => ({ ...prev, password: "" }));
  };

  const handleSaveEdit = async () => {
    try {
      setSavingEdit(true);
      const payload = {
        name: editForm.name,
        lastName: editForm.lastName,
        hireDate: editForm.hireDate || null,
        revenuePercentage: editForm.revenuePercentage,
        username: editForm.username,
      };
      if (editForm.password) payload.password = editForm.password;

      const updated = await updateDriver(editForm.id, payload);
      setDrivers((prev) =>
        prev.map((d) =>
          d.id === editForm.id
            ? {
                ...d,
                name: updated.name,
                lastName: updated.lastName,
                hireDate: updated.hireDate
                  ? new Date(updated.hireDate).toLocaleDateString()
                  : "—",
                hireDateRaw: updated.hireDate
                  ? new Date(updated.hireDate).toISOString().slice(0, 10)
                  : "",
                username: updated.user?.username || payload.username || d.username,
                userId: updated.user?._id || d.userId,
                revenuePercentage:
                  updated.revenuePercentage ?? payload.revenuePercentage ?? d.revenuePercentage,
              }
            : d
        )
      );
      setSnack({ open: true, message: "Conductor actualizado", severity: "success" });
      setEditOpen(false);
      setEditForm((prev) => ({ ...prev, password: "" }));
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    } finally {
      setSavingEdit(false);
    }
  };

  const columns = [
    { field: "name", headerName: "Nombre", flex: 1 },
    { field: "lastName", headerName: "Apellido", flex: 1 },
    { field: "hireDate", headerName: "Fecha de Alta", flex: 1 },
    { field: "revenuePercentage", headerName: "Recaudación (%)", type: "number", flex: 0.9 },
    { field: "licensesCount", headerName: "Licencias", type: "number", flex: 0.8 },
    { field: "closuresCount", headerName: "Cierres Contables", type: "number", flex: 0.8 },
    { field: "totalEarned", headerName: "Ganancias Totales", type: "number", flex: 1 },
    { field: "totalKmDriven", headerName: "Km Totales", type: "number", flex: 1 },
    { field: "daysWorked", headerName: "Días Trabajados", type: "number", flex: 0.8 },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuOpen(e, params.row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const data = await getDriversByBoss();
        const rows = (data || []).map((d, idx) => {
          const hireDateRaw = d.hireDate ? new Date(d.hireDate).toISOString().slice(0, 10) : "";
          return {
            id: d._id || `drv_${idx}`,
            name: d.name ?? "—",
            lastName: d.lastName ?? "—",
            hireDate: hireDateRaw ? new Date(hireDateRaw).toLocaleDateString() : "—",
            hireDateRaw,
            licensesCount: d.licenses?.length ?? 0,
            closuresCount: d.accountingClosures?.length ?? 0,
            totalEarned: d.totalEarned ?? 0,
            totalKmDriven: d.totalKmDriven ?? 0,
            daysWorked: d.daysWorked ?? 0,
            revenuePercentage: d.revenuePercentage ?? 40,
            username: d.user?.username || "",
            userId: d.user?._id || d.user || "",
          };
        });
        setDrivers(rows);
      } catch (err) {
        setSnack({ open: true, message: err.message, severity: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchDrivers();
  }, []);

  // KPIs
  const totalConductores = drivers.length;
  const conLicencia = drivers.filter((d) => (d.licensesCount || 0) > 0).length;
  const sinLicencia = totalConductores - conLicencia;

  return (
    <Box sx={{ m: "20px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Header title="Conductores" subtitle="Gestión de conductores registrados" />

      {/* Widgets */}
      <Box
        sx={{
          mt: 1,
          mb: 1,
          display: "flex",
          gap: 2,
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "nowrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<GroupIcon />}
            label="Total conductores"
            value={totalConductores}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<BadgeIcon />}
            label="Con licencia asignada"
            value={conLicencia}
            palette={palette}
            highlight
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard
            icon={<DirectionsCarIcon />}
            label="Sin licencia"
            value={sinLicencia}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
      </Box>

      {/* Card + DataGrid full height */}
      <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", overflowX: "hidden" }}>
        <Card
          title="Listado de conductores"
          palette={palette}
          action={
            <Button
              variant="contained"
              onClick={() => navigate("/drivers/register")}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              Añadir Conductor
            </Button>
          }
        >
          <Box sx={{ ...dgSx(palette), overflowX: isMobile ? "auto" : "visible" }}>
            <DataGrid
              autoHeight={isMobile}
              rows={drivers}
              columns={columns}
              loading={loading}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              rowHeight={52}
              columnHeaderHeight={48}
              disableRowSelectionOnClick
              sx={{ height: isMobile ? "auto" : "100%", width: "100%" }}
              columnVisibilityModel={
                isMobile
                  ? {
                      hireDate: false,
                      revenuePercentage: false,
                      closuresCount: false,
                      totalEarned: false,
                      totalKmDriven: false,
                      daysWorked: false,
                    }
                  : undefined
              }
            />
          </Box>
        </Card>
      </Box>

      {/* Menú acciones fila */}
      <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
        <MenuItem onClick={handleOpenModal}>Asignar Licencia</MenuItem>
        <MenuItem onClick={handleOpenEdit}>Editar conductor</MenuItem>
      </Menu>

      {/* Modal de asignación */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: modalCardBg,
            border: `1px solid ${modalBorder}`,
            color: modalText,
            boxShadow:
              theme.palette.mode === "light"
                ? "0 10px 30px rgba(15,23,42,0.18)"
                : "0 14px 40px rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogTitle sx={{ color: modalText, fontWeight: 700 }}>
          Selecciona una Licencia
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: modalBorder }}>
          {licenses.length === 0 && (
            <Typography sx={{ color: palette.label, py: 1 }}>No hay licencias disponibles.</Typography>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr" }, gap: 1 }}>
            {licenses.map((lic) => {
              const checked = selectedLicenses.includes(lic._id);
              return (
                <FormControlLabel
                  key={lic._id}
                  value={lic._id}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setSelectedLicenses((prev) =>
                          checked ? [...prev, lic._id] : prev.filter((id) => id !== lic._id)
                        );
                      }}
                      sx={{ color: modalText }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: modalText, fontWeight: 700 }}>
                        {lic.licenseNumber}
                      </Typography>
                      <Typography sx={{ color: palette.label }}>
                        {lic.vehicle?.licensePlate || "Sin matrícula"}
                      </Typography>
                    </Box>
                  }
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleCloseModal} sx={{ color: modalText, textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!selectedLicenses.length}
            onClick={handleAssign}
            sx={{
              backgroundColor: palette.btn,
              color: "white",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: palette.btnHover },
            }}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de edición */}
      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: modalCardBg,
            border: `1px solid ${modalBorder}`,
            color: modalText,
            boxShadow:
              theme.palette.mode === "light"
                ? "0 10px 30px rgba(15,23,42,0.18)"
                : "0 14px 40px rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogTitle sx={{ color: modalText, fontWeight: 700 }}>
          Editar conductor
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: modalBorder }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveEdit();
            }}
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
            pt={0.5}
          >
            <TextField
              label="Nombre"
              name="name"
              value={editForm.name}
              onChange={handleEditChange}
              required
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Apellidos"
              name="lastName"
              value={editForm.lastName}
              onChange={handleEditChange}
              required
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Fecha de alta"
              type="date"
              name="hireDate"
              value={editForm.hireDate}
              onChange={handleEditChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Porcentaje recaudación (%)"
              type="number"
              name="revenuePercentage"
              value={editForm.revenuePercentage}
              onChange={handleEditChange}
              inputProps={{ min: 0, max: 100, step: 1 }}
              required
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Usuario"
              name="username"
              value={editForm.username}
              onChange={handleEditChange}
              required
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Contraseña (solo si se cambia)"
              type="password"
              name="password"
              value={editForm.password}
              onChange={handleEditChange}
              fullWidth
              sx={modalInputStyles}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={savingEdit} sx={{ color: modalText }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={savingEdit || !editForm.name || !editForm.lastName || !editForm.username}
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: "white",
              textTransform: "none",
              "&:hover": { backgroundColor: colors.blueAccent[400] },
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%", fontSize: "1rem" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Drivers;
